import React, {
  FC,
  createContext,
  useContext,
  useState,
  useEffect,
} from 'react';
import { useMutation } from '@apollo/react-hooks';
import { getClientEnvironmentDetails } from '@sm/utils';
import { clientErrorHandler } from '@sm/webassets';

import recordContentModuleEvent from './RecordContentModuleEvent.graphql';
import { ContentModuleEvent } from '../types/enums';
import { ContentModuleData, ContentModuleProperties } from '../types/types';

const ContentModuleContext = createContext({} as ContentModuleData);

type ContentModuleContextProviderProps = {
  moduleData: ContentModuleData;
};

const ContentModuleContextProvider: FC<ContentModuleContextProviderProps> = props => {
  const { moduleData } = props;
  return <ContentModuleContext.Provider value={{ ...moduleData }} {...props} />;
};

function useContentModuleEvent(
  properties?: ContentModuleProperties
): (
  event: ContentModuleEvent,
  eventProperties?: ContentModuleProperties
) => void {
  const { name, variation } = useContext(ContentModuleContext);
  const [renderTime, setRenderTime] = useState(new Date());
  const { isBrowser } = getClientEnvironmentDetails();
  const pageUrl = isBrowser ? window.location.href : '/';

  const [recordEvent] = useMutation(recordContentModuleEvent, {
    onError: error => {
      clientErrorHandler.logError(error, 'recordContentModuleEvent');
    },
  });

  useEffect(() => {
    setRenderTime(new Date());
  }, []);

  return (
    event: ContentModuleEvent,
    eventProperties?: ContentModuleProperties
  ): void => {
    const mergedProperties: ContentModuleProperties = {
      ...properties,
      ...eventProperties,
    };
    recordEvent({
      variables: {
        input: {
          event,
          moduleName: name,
          pageUrl,
          properties: mergedProperties,
          timeToClick: (new Date().valueOf() - renderTime.valueOf()) / 1000,
          variation,
        },
      },
    });
  };
}

export { useContentModuleEvent, ContentModuleContextProvider };
